import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, NgZone, OnInit, output, viewChild } from '@angular/core';
import { ProjectEstimateSummaryComponent } from '@app/cem/project/estimate/project-estimate-summary/project-estimate-summary.component';
import { ProjectState } from '@app/cem/project/services/state/project-state';
import { TabStripScrollingDirective } from '@app/shared/directives/kendohelpers/tabstrip-scrolling.directive';
import { ProjectEstimate } from '@app/shared/models';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { ProjectBaseScenarioTabItemComponent } from 'app/cem/project/content/project-base-scenario-tabitem.component';
import { ProjectEstimateStatus, ProjectEstimateStatusDescription, ProjectStatusId } from 'app/shared/data/project-enums';
import { ProjectResourcesComponent } from '../../content/project-resources/project-resources.component';
import { EstimateState } from '../../models/estimate-state';
import { EstimateTab } from '../../models/estimate-tab';
import { EstimateTabService } from '../../services/estimate-tab.service';
import { ProjectEstimateDetailComponent } from '../project-estimate-detail/project-estimate-detail.component';

@Component({
  selector: 'app-project-estimate-home',
  templateUrl: './project-estimate-home.component.html',
  styleUrls: ['./project-estimate-home.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProjectEstimateDetailComponent,
    ProjectEstimateSummaryComponent,
    ProjectResourcesComponent,
    TabStripModule,
    TabStripScrollingDirective,
  ],
})

export class ProjectEstimateHomeComponent extends ProjectBaseScenarioTabItemComponent implements OnInit {
  private estimateTabService = inject(EstimateTabService);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private state = inject(ProjectState);

  public readonly tabstrip = viewChild<any>('tabstrip');
  readonly projectEstimateSummaryComponent = viewChild(ProjectEstimateSummaryComponent);
  readonly projectEstimateDetailComponent = viewChild(ProjectEstimateDetailComponent);


  // tabTitle modifies the title of this Tab to indicate unsaved edits
  readonly tabTitle = output<string>();
  // tabTdv provides touched-dirty-valid status of the form in this tab
  readonly tabTdv = output<boolean>();

  public tabItems = new Array<EstimateTab>();
  public initialItem: string = 'Estimate2';
  public refreshSummaryPage: boolean;
  public estimateState: EstimateState;
  public disableAllEdits: boolean;

  constructor() {
    super();

    this.estimateTabService.estimateTab$.subscribe(
      tab => {
        let z = this.tabItems.findIndex(x => x.TabTitle === tab.TabTitle);
        // estimateTabService is shared among all projects. Prevent cross-talk among the projects by
        // checking the project Id of the tab Item before pushing into the tabItems array for this project.
        if (z === -1 && tab.ProjectId === this.projectTab().ProjectId) {
          this.tabItems.push(tab);
          this.selectTabAsync(this.tabItems.length + 1); //normally you subtract 1 but there's a built-in home tab
        }
      }
    );

    this.estimateTabService.estimateTabUpdated$.subscribe((estimates: ProjectEstimate[]) => {
      let tabItemsById = new Map<number, EstimateTab>(this.tabItems.map(e => [e.ProjectEstimate.ProjectEstimateId, e]));
      estimates.forEach((e) => {
        let tabItem = tabItemsById.get(e.ProjectEstimateId);
        if (tabItem) {
          Object.assign(tabItem.ProjectEstimate, e);
        }
      });
      this.cdr.detectChanges();
    }
    );
  }

  selectTabAsync(index: number) {
    // this will select it on the next zone refresh
    this.desiredTabIndex = index;
    this.zone.onMicrotaskEmpty.emit(null);
    this.cdr.detectChanges();
  }

  private desiredTabIndex = -1;

  ngOnInit() {
    this.refreshSummaryPage = false;
    let status = this.state.data.project.ProjectStatusId;
    if (status === ProjectStatusId.Closed || status === ProjectStatusId.Cancelled) {
      this.disableAllEdits = true;
    } else {
      this.disableAllEdits = false;
    }

    this.projectTab().ProjectChanged.subscribe(proj => {
      let index = this.tabItems.findIndex(f => f.Status === ProjectEstimateStatusDescription.Draft);
      if (([ProjectStatusId.Closed, ProjectStatusId.Cancelled].includes(proj.ProjectStatusId)) && index > -1) {
        this.closeDraftTab();
        this.disableAllEdits = true;
        this.tabstrip().selectTab(0);
      }
    });

    this.zone.onMicrotaskEmpty.subscribe({
      next: () => {
        this.zone.run(() => {
          if (this.desiredTabIndex > 0) {
            this.tabstrip().selectTab(this.desiredTabIndex);
            this.desiredTabIndex = -1;

          }
        });
      }
    });
  }

  public closeTab(index, e) {
    this.tabstrip().selectTab(0);
    let y: any = this.tabItems.findIndex(z => z === e);
    this.tabItems.splice(y, 1);
  }

  public runTabActivity(): void {
    const projectEstimateDetailComponent = this.projectEstimateDetailComponent();
    if (projectEstimateDetailComponent && projectEstimateDetailComponent.estimateStatusId() == ProjectEstimateStatus.Draft) {
      projectEstimateDetailComponent.runTabActivity();
    }
  }

  public readProjectEstimateState(data) {
    this.estimateState = data;
  }

  public closeDraftTab(): void {
    let index = this.tabItems.findIndex(f => f.Status === ProjectEstimateStatusDescription.Draft);

    if (index > -1) {
      this.tabItems.splice(index, 1);
    }
  }

  public readEstimateTab(e: string): void {
    this.tabTitle.emit(e);
  }

  public readEstimateTabTdv(e: boolean): void {
    this.tabTdv.emit(e);
  }

  public reloadEstimatesSummary() {
      this.projectEstimateSummaryComponent().getEstimates()
  }

}